import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BookOpen, IndianRupee, Wallet, Copy } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, Badge, statusTone, Money, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function InstituteDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/franchise/institute/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading rows={4} />;
  const referralUrl = `${window.location.origin}/register?ref=${d.referral_code}`;
  const copy = () => { navigator.clipboard.writeText(referralUrl); toast.success("Enrollment link copied"); };
  return (
    <div data-testid="institute-dashboard">
      <PageHeader title={d.institute_name || "Institute Dashboard"} subtitle={`You earn ${d.institute_percent}% of eligible commissionable revenue`} action={<Link to="/institute/students"><Button className="viriks-amber text-white" data-testid="inst-enroll-cta">Enroll a student</Button></Link>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total students" value={d.total_students} icon={Users} testid="inst-stat-students" />
        <StatCard label="Active enrollments" value={d.active_enrollments} icon={BookOpen} testid="inst-stat-active" />
        <StatCard label="Eligible commissions" value={<Money amount={d.eligible} />} icon={IndianRupee} accent="bg-amber" testid="inst-stat-eligible" />
        <StatCard label="Available balance" value={<Money amount={d.available_balance} />} icon={Wallet} accent="bg-emerald-600" testid="inst-stat-balance" />
      </div>
      <div className="bg-white border border-border rounded-lg p-5 mb-8 flex flex-wrap items-center gap-3" data-testid="inst-referral">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-muted-foreground">Institute code <span className="font-mono font-bold text-navy">{d.referral_code}</span> — students who register with this link are linked to your institute</div>
          <div className="font-mono text-sm truncate mt-1">{referralUrl}</div>
        </div>
        <Button variant="outline" onClick={copy} data-testid="inst-copy-link"><Copy size={15} className="mr-1" /> Copy link</Button>
      </div>
      {d.pending > 0 && <p className="text-sm text-muted-foreground mb-6"><Money amount={d.pending} /> in commissions is pending confirmation by VIRIKS.</p>}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Recent student enrollments" empty="No enrollments yet." items={d.recent_enrollments} render={(e) => (
          <><span className="font-medium text-navy truncate">{e.student_name}</span><span className="text-muted-foreground truncate">{e.course_title}</span><span className="text-xs">{new Date(e.enrolled_at).toLocaleDateString("en-IN")}</span></>
        )} />
        <Panel title="Commission transactions" empty="No commissions yet." items={d.recent_transactions} render={(t) => (
          <><span className="truncate">{t.course_title}</span><span className="font-semibold"><Money amount={t.amount} /></span><Badge tone={statusTone(t.status)}>{t.status}</Badge></>
        )} />
      </div>
    </div>
  );
}

function Panel({ title, items, render, empty }) {
  return (
    <div className="bg-white border border-border rounded-lg p-5">
      <h3 className="font-heading font-bold text-navy mb-3">{title}</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : items.map((x) => (
        <div key={x.id} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center py-2.5 border-t border-border text-sm">{render(x)}</div>
      ))}
    </div>
  );
}
